// src/components/AssignSingleMemberPhotoPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { auth, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faCamera, faTrash, faCheck, faSpinner, faImages,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import MemberPhotoAvatar from './MemberPhotoAvatar';

import './AssignSingleMemberPhotoPage.css';

// Helper to get orgId from the current user's token claims
async function getOrgId() {
    if (!auth.currentUser) return null;
    try {
        const token = await auth.currentUser.getIdTokenResult(false);
        return token.claims.orgId || null;
    } catch (error) {
        console.error("Error getting user organization ID:", error);
        return null;
    }
}

// --- Main Component ---
function AssignSingleMemberPhotoPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { familyId } = useParams();

    // Data passed from the previous page. forMemberId is optional.
    const { forMemberName, forMemberId, currentPhotoPath, returnPath } = location.state || {};

    const [extractedPhotos, setExtractedPhotos] = useState([]);
    const [selectedGridPhotoPath, setSelectedGridPhotoPath] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Initialize Firebase Cloud Function
    const firebaseFunctions = getFunctions();
    const updateMemberV2Func = httpsCallable(firebaseFunctions, 'updateMemberV2');

    // Effect to fetch available photos from Firebase Storage
    useEffect(() => {
        let isMounted = true;
        const fetchPhotos = async () => {
            if (!familyId) {
                if (isMounted) { setError("Family ID is missing from the URL."); setLoading(false); }
                return;
            }
            const orgId = await getOrgId();
            if (!orgId) {
                if (isMounted) { setError("Could not verify your organization. Please log in again."); setLoading(false); }
                return;
            }
            setLoading(true);
            try {
                const photosPath = `${orgId}/family/${familyId}/members/`;
                const listResult = await listAll(ref(storage, photosPath));
                const photosDataPromises = listResult.items.map(async (itemRef) => ({
                    path: itemRef.fullPath,
                    url: await getDownloadURL(itemRef),
                }));
                const photosData = await Promise.all(photosDataPromises);
                if (isMounted) setExtractedPhotos(photosData);
            } catch (e) {
                if (e.code === 'storage/object-not-found') {
                    if (isMounted) setExtractedPhotos([]);
                } else {
                    console.error("Error fetching extracted photos:", e);
                    if (isMounted) setError("Error loading available photos.");
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchPhotos();
        return () => { isMounted = false; };
    }, [familyId]);

    // --- Action Handlers ---

    // This function now handles both ADD and EDIT scenarios
    const handleConfirmSelection = async (newPhotoPath) => {
        if (!returnPath) return;
        setIsUpdating(true);
        setError(null);

        // If forMemberId exists, we are EDITING an existing member. Update DB directly.
        if (forMemberId) {
            try {
                const result = await updateMemberV2Func({ memberId: forMemberId, photo: newPhotoPath });
                if (!result.data.success) {
                    throw new Error(result.data.message || 'Failed to update photo on the server.');
                }
                // Navigate back and tell the previous page to refresh
                navigate(returnPath, { state: { photoUpdated: true } });
            } catch (err) {
                setError(err.message || "An unexpected error occurred while updating the photo.");
                setIsUpdating(false);
            }
        } else {
            // If no forMemberId, we are ADDING a new member. Just pass the path back.
            navigate(returnPath, { state: { ...location.state, newPhotoPath: newPhotoPath } });
        }
    };

    const handleSetNewPhoto = () => {
        if (!selectedGridPhotoPath) return;
        handleConfirmSelection(selectedGridPhotoPath);
    };

    const handleRemovePhoto = () => {
        const confirm = window.confirm(`Are you sure you want to remove the photo for ${forMemberName}?`);
        if (confirm) {
            handleConfirmSelection(null); // Pass null to remove the photo
        }
    };

    const handleBack = () => {
        if (returnPath) {
            navigate(returnPath, { state: location.state });
        } else {
            navigate(`/families/${familyId}`);
        }
    };

    // --- Render Logic ---

    // Guard clause now allows forMemberId to be missing.
    if (!forMemberName || !returnPath) {
        return (
            <div className="assign-photo-page">
                <div className="assign-photo-header">
                    <div className="container">
                        <div className="assign-photo-header-content">
                            <div className="assign-photo-header-left">
                                <Link to={`/families/${familyId}`} className="assign-photo-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="assign-photo-title">Error</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="assign-photo-content">
                        <div className="assign-photo-alert assign-photo-alert-danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="assign-photo-alert-icon" />
                            <div className="assign-photo-alert-content">
                                Required data (Member Name or Return Path) was not provided. Please go back and try again.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="assign-photo-page">
                <div className="assign-photo-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="assign-photo-page">
            {/* Page Header */}
            <div className="assign-photo-header">
                <div className="container">
                    <div className="assign-photo-header-content">
                        <div className="assign-photo-header-left">
                            <button onClick={handleBack} className="assign-photo-back-btn" title="Go Back">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                            <div>
                                <h1 className="assign-photo-title">
                                    <FontAwesomeIcon icon={faCamera} style={{ marginRight: '0.5rem' }} />
                                    Set Photo
                                </h1>
                                <p className="assign-photo-subtitle">Assign a photo for {forMemberName}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="assign-photo-content">
                    {/* Error Alert */}
                    {error && (
                        <div className="assign-photo-alert assign-photo-alert-danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="assign-photo-alert-icon" />
                            <div className="assign-photo-alert-content">{error}</div>
                            <button className="assign-photo-alert-close" onClick={() => setError(null)}>
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Current Photo Section */}
                    <div className="assign-photo-current">
                        <div className="assign-photo-current-header">
                            <h2 className="assign-photo-current-title">Current Photo</h2>
                        </div>
                        <div className="assign-photo-current-body">
                            <MemberPhotoAvatar
                                src={currentPhotoPath}
                                alt={`Current photo of ${forMemberName}`}
                                size={120}
                                className="assign-photo-avatar"
                            />
                            {currentPhotoPath && (
                                <button
                                    className="assign-photo-remove-btn"
                                    onClick={handleRemovePhoto}
                                    disabled={isUpdating}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Remove Current Photo
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Photo Grid Section */}
                    <div className="assign-photo-grid-section">
                        <div className="assign-photo-grid-header">
                            <h2 className="assign-photo-grid-title">Select from Available Photos</h2>
                        </div>
                        <div className="assign-photo-grid-body">
                            {extractedPhotos.length === 0 ? (
                                <div className="assign-photo-empty">
                                    <div className="assign-photo-empty-icon">
                                        <FontAwesomeIcon icon={faImages} />
                                    </div>
                                    <p className="assign-photo-empty-text">
                                        No photos available for this family. Upload photos to the family page to select them here.
                                    </p>
                                </div>
                            ) : (
                                <div className="assign-photo-grid">
                                    {extractedPhotos.map(photo => (
                                        <div
                                            key={photo.path}
                                            className={`assign-photo-grid-item ${selectedGridPhotoPath === photo.path ? 'selected' : ''}`}
                                            onClick={() => setSelectedGridPhotoPath(photo.path)}
                                        >
                                            <img src={photo.url} alt="Family member" />
                                            {selectedGridPhotoPath === photo.path && (
                                                <div className="selected-check">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        <div className="assign-photo-action">
                            <button
                                className="assign-photo-submit-btn"
                                onClick={handleSetNewPhoto}
                                disabled={!selectedGridPhotoPath || isUpdating}
                            >
                                {isUpdating ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheck} />
                                        Set as Member Photo
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AssignSingleMemberPhotoPage;
